import { z } from 'zod';

import type { RoleFE } from '../systemAdmin';
import type { MsClaimTO, MsRoleTO } from './msWireSpec';
import { makeSanitizeWire, mapListPerEntry, safeOptional } from './restMapping';

/**
 * REST -> domain mapping for roles, served by the management service's `AuthControllerApi`:
 * `GET /api/v1/auth/roles` returns `RoleTO[]` -> {@link rolesFromRestV1}. `V1` is the REST api
 * version.
 */

const label = (field: string) => `roleFromRestV1.${field}`;

/** `createdAt` / `updatedAt` — camelCase, which is why the shared `sanitizeWire` is not used. */
const ROLE_DATE_KEYS = new Set(['createdAt', 'updatedAt']);

const sanitizeRoleWire = makeSanitizeWire(ROLE_DATE_KEYS);

const WireClaimObjectSchema = z.object({
    pattern: safeOptional(z.string(), label('claims[].pattern')),
    createdAt: safeOptional(z.string(), label('claims[].createdAt')),
    updatedAt: safeOptional(z.string(), label('claims[].updatedAt')),
});

const WireRoleObjectSchema = z.object({
    id: z.string().min(1),
    name: safeOptional(z.string(), label('name')),
    createdAt: safeOptional(z.string(), label('createdAt')),
    updatedAt: safeOptional(z.string(), label('updatedAt')),
    claims: safeOptional(z.array(WireClaimObjectSchema), label('claims')),
    useWildcardClaims: safeOptional(z.boolean(), label('useWildcardClaims')),
});

/**
 * Wire schema for a `RoleTO`. Exported so `mocks/tests/restContract.test.ts` can assert a mock body
 * against the same validator the mapper uses, as it already does for `WireHubSchema`.
 */
export const WireRoleSchema = z.preprocess(sanitizeRoleWire, WireRoleObjectSchema);

type WireRoleObject = z.infer<typeof WireRoleObjectSchema>;
type WireClaimObject = z.infer<typeof WireClaimObjectSchema>;

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type RoleWireKeysExistOnSpec = {
    role: KeysExistOn<WireRoleObject, MsRoleTO>;
    claim: KeysExistOn<WireClaimObject, MsClaimTO>;
};

export const roleFromRestV1 = (raw: unknown): RoleFE => {
    const wire = WireRoleSchema.parse(raw);
    return {
        id: wire.id,
        name: wire.name ?? '',
    };
};

export const rolesFromRestV1 = (raw: unknown): RoleFE[] => mapListPerEntry(raw, roleFromRestV1, label('list'));
